/**
 * Drops-tab tag display: a tag whose rule has BOTH an emoji and a drop-scoped
 * (source_* or list_*) condition renders as a bare, slightly larger,
 * rule-colored emoji (no chip background). All other tags stay normal chips
 * under their ORIGINAL name so color/style lookups and the tag filter keep
 * working. In grouped views scoped-rule tags are hidden at display time only
 * — the row data (and thus the tag filter) still carries them.
 * For Media Devoted | Created By Ciro Autiero
 */

export interface DropEmojiTag {
  emoji: string;
  /** Rule name(s) behind this emoji — shown as the hover title. */
  title: string;
  /** The rule's color, applied to the emoji glyph itself (no background). */
  color?: string;
}

export interface DropTagDisplay {
  /** Bare emojis rendered without a chip — ONE glyph per matching scoped
   *  rule, so "+ +" means two rules fired (e.g. profitable source AND list). */
  emojiTags: DropEmojiTag[];
  /** Normal chip labels (original rule names). */
  chipTags: string[];
}

export interface SplitDropTagsOptions {
  tagEmojis?: Record<string, string>;
  /** Rules with drop-scoped (source_* or list_*) conditions. */
  scopedRuleNames?: string[];
  tagColors?: Record<string, string>;
  /** Grouped views: hide scoped-rule tags from display entirely. */
  hideScopedTags?: boolean;
}

export function splitDropTagsForDisplay(
  tags: string[],
  opts: SplitDropTagsOptions,
): DropTagDisplay {
  const scopedRules = new Set(opts.scopedRuleNames ?? []);
  const emojiTags: DropEmojiTag[] = [];
  const chipTags: string[] = [];
  for (const tag of tags) {
    if (scopedRules.has(tag)) {
      if (opts.hideScopedTags) continue;
      const emoji = opts.tagEmojis?.[tag]?.trim();
      if (emoji) {
        if (!emojiTags.some((e) => e.title === tag)) {
          emojiTags.push({ emoji, title: tag, color: opts.tagColors?.[tag] });
        }
        continue;
      }
    }
    if (!chipTags.includes(tag)) chipTags.push(tag);
  }
  return { emojiTags, chipTags };
}
